import { getOriginalDiffKey, removeBlock, reuseDocumentKeys } from '@gitbook/doc-core';
import { Block, Document, Fragment, Value } from '@gitbook/slate';

/**
 * Result kinds returned by {@link revertDocumentBlockChange}.
 */
export type RevertDocumentBlockChangeKind = 'removed' | 'restored' | 'replaced' | 'moved';

/**
 * Error codes for document block revert failures.
 */
export type RevertDocumentBlockChangeErrorCode =
    | 'missing_block'
    | 'missing_parent'
    | 'key_conflict'
    | 'no_change';

/**
 * Error thrown when a document block revert cannot be applied.
 */
export class RevertDocumentBlockChangeError extends Error {
    /**
     * Machine-readable reason for the revert failure.
     */
    readonly code: RevertDocumentBlockChangeErrorCode;

    constructor(code: RevertDocumentBlockChangeErrorCode, message: string) {
        super(`RevertDocumentBlockChangeError: ${message}`);
        this.code = code;
    }
}

/**
 * Revert one block-level change from a current document back toward a base document.
 *
 * The `blockKey` comes from the rendered diff, which reconciles the head document onto the base's
 * key-space (`diffDocuments` → `reuseDocumentKeys(base, head)`). The raw current/working document,
 * however, keeps its own (head) keys, so a modified block can live under a different key in each
 * document. We therefore align the current document to the base key-space before resolving the
 * block, then restore the current document's own keys on the result so unrelated blocks keep their
 * identity (e.g. comment anchors). `equalsBase` is computed in the aligned (base) key-space, where
 * content equality is meaningful, so the caller can short-circuit to pointing the page at the base
 * document when the whole document is back to base.
 */
export function revertDocumentBlockChange(input: {
    currentDocument: Document;
    baseDocument: Document;
    blockKey: string;
}): {
    document: Document;
    kind: RevertDocumentBlockChangeKind;
    blockKey: string;
    equalsBase: boolean;
} {
    const alignedCurrent = reuseDocumentKeys(input.baseDocument, input.currentDocument);
    const reverted = revertAlignedDocumentBlock({
        currentDocument: alignedCurrent,
        baseDocument: input.baseDocument,
        blockKey: input.blockKey,
    });

    return {
        document: reuseDocumentKeys(input.currentDocument, reverted.document),
        kind: reverted.kind,
        blockKey: reverted.blockKey,
        equalsBase: reverted.document.equals(input.baseDocument),
    };
}

/**
 * Core revert logic, operating on a current document that has already been aligned to the base
 * key-space so `blockKey` resolves consistently in both documents.
 */
function revertAlignedDocumentBlock(input: {
    currentDocument: Document;
    baseDocument: Document;
    blockKey: string;
}): {
    document: Document;
    kind: RevertDocumentBlockChangeKind;
    blockKey: string;
} {
    const blockKey = getOriginalDiffKey(input.blockKey);
    const baseBlock = getDocumentBlock(input.baseDocument, blockKey);
    const currentBlock = getDocumentBlock(input.currentDocument, blockKey);

    if (!baseBlock && !currentBlock) {
        throw new RevertDocumentBlockChangeError(
            'missing_block',
            `Block ${blockKey} was not found in the current or base document`
        );
    }

    if (!baseBlock && currentBlock) {
        return {
            document: removeAddedDocumentBlock(input.currentDocument, blockKey),
            kind: 'removed',
            blockKey,
        };
    }

    if (baseBlock && !currentBlock) {
        return {
            document: restoreDocumentBlock({
                currentDocument: input.currentDocument,
                baseDocument: input.baseDocument,
                baseBlock,
            }),
            kind: 'restored',
            blockKey,
        };
    }

    if (!baseBlock || !currentBlock) {
        throw new RevertDocumentBlockChangeError(
            'missing_block',
            `Block ${blockKey} was not found in the current or base document`
        );
    }

    const currentPath = input.currentDocument.getPath(blockKey);
    const basePath = input.baseDocument.getPath(blockKey);
    const isMoved = !pathsEqual(currentPath, basePath);

    if (baseBlock.equals(currentBlock) && !isMoved) {
        throw new RevertDocumentBlockChangeError(
            'no_change',
            `Block ${blockKey} does not differ from the base document`
        );
    }

    return {
        document: replaceDocumentBlock({
            currentDocument: input.currentDocument,
            baseDocument: input.baseDocument,
            baseBlock,
            currentBlock,
        }),
        kind: baseBlock.equals(currentBlock) ? 'moved' : 'replaced',
        blockKey,
    };
}

function getDocumentBlock(document: Document, blockKey: string): Block | null {
    const node = document.getNode(blockKey);
    return Block.isBlock(node) ? node : null;
}

/**
 * Remove a block that only exists in the current document (a fully-added block). Uses doc-core's
 * `removeBlock` rather than a bare `removeNodeByKey` so that removing the sole item of a list also
 * drops the now-empty list — matching how the editor removes blocks everywhere else.
 */
function removeAddedDocumentBlock(document: Document, blockKey: string): Document {
    return removeBlock(Value.create({ document }).change(), blockKey).value.document;
}

function restoreDocumentBlock(input: {
    currentDocument: Document;
    baseDocument: Document;
    baseBlock: Block;
}): Document {
    assertNoKeyConflict(input.currentDocument, input.baseBlock);

    return insertBaseBlockAtOriginalPosition(input);
}

/**
 * Swap a modified or moved block for its base version, at the position the base document gives it.
 *
 * Slate's own `replaceNodeByKey` cannot express this: it re-inserts under the same parent at the
 * same index, which covers an edit in place but not a block that also moved. So this removes and
 * re-inserts, and the removal defers normalization: the block may be the only child of a container
 * the schema requires to be non-empty (a hint, a list), and repairing that container while it is
 * momentarily empty is what used to swallow the replacement and leave the hint blank.
 */
function replaceDocumentBlock(input: {
    currentDocument: Document;
    baseDocument: Document;
    baseBlock: Block;
    currentBlock: Block;
}): Document {
    const { currentDocument, baseDocument, baseBlock, currentBlock } = input;
    const placement = getBaseBlockPlacement({ currentDocument, baseDocument, baseBlock });

    // An edit can invert a parent/child pair, so the base parent may now sit inside the very block
    // being replaced. Removing the block would take the destination with it, and `insertNodeByKey`
    // does nothing at all when its target has no path, giving a revert that reports success
    // having deleted the whole subtree.
    if (currentBlock.hasDescendant(placement.parentKey)) {
        throw new RevertDocumentBlockChangeError(
            'missing_parent',
            `Cannot restore block ${baseBlock.key} beneath the block being replaced`
        );
    }

    const currentParent = currentDocument.getParent(currentBlock.key);
    const sameParent = currentParent?.key === placement.parentKey ? currentParent : null;
    // `placement.index` is measured before the removal, so within one parent it counts a block that
    // is about to disappear from an earlier position.
    const insertionIndex =
        sameParent && sameParent.getChildIndex(currentBlock.key) < placement.index
            ? placement.index - 1
            : placement.index;

    const change = Value.create({ document: currentDocument })
        .change()
        .removeNodeByKey(currentBlock.key, { normalize: false });

    assertNoKeyConflict(change.value.document, baseBlock);

    change.insertNodeByKey(placement.parentKey, insertionIndex, baseBlock);

    // `insertNodeByKey` returns quietly on a key conflict or a missing destination path rather than
    // throwing, so confirm the block landed instead of handing back a document it was dropped from.
    if (!change.value.document.getNode(baseBlock.key)) {
        throw new RevertDocumentBlockChangeError(
            'missing_parent',
            `Block ${baseBlock.key} could not be inserted under parent ${placement.parentKey}`
        );
    }

    // Inserting normalizes the destination and its ancestors, never a container the block has just
    // left, which for a cross-parent move can be a sibling. Normalization may already have removed
    // that container, so only repair one that is still there.
    if (currentParent && !sameParent && change.value.document.getNode(currentParent.key)) {
        change.normalizeNodeByKey(currentParent.key);
    }

    return change.value.document;
}

function insertBaseBlockAtOriginalPosition(input: {
    currentDocument: Document;
    baseDocument: Document;
    baseBlock: Block;
}): Document {
    const { currentDocument, baseDocument, baseBlock } = input;
    const placement = getBaseBlockPlacement({ currentDocument, baseDocument, baseBlock });

    return Value.create({ document: currentDocument })
        .change()
        .insertNodeByKey(placement.parentKey, placement.index, baseBlock).value.document;
}

function getBaseBlockPlacement(input: {
    currentDocument: Document;
    baseDocument: Document;
    baseBlock: Block;
}): { parentKey: string; index: number } {
    const { currentDocument, baseDocument, baseBlock } = input;
    const baseParent = baseDocument.getParent(baseBlock.key);

    if (!baseParent || !isDocumentBlockParent(baseParent)) {
        throw new RevertDocumentBlockChangeError(
            'missing_parent',
            `Parent for block ${baseBlock.key} was not found in the base document`
        );
    }

    const currentParent =
        baseParent.key === baseDocument.key
            ? currentDocument
            : currentDocument.getNode(baseParent.key);

    if (!currentParent || !isDocumentBlockParent(currentParent)) {
        throw new RevertDocumentBlockChangeError(
            'missing_parent',
            `Parent for block ${baseBlock.key} was not found in the current document`
        );
    }

    const baseSiblingKeys = baseParent.nodes.map((node) => node.key).toArray();
    const baseIndex = baseSiblingKeys.indexOf(baseBlock.key);

    if (baseIndex < 0) {
        throw new RevertDocumentBlockChangeError(
            'missing_block',
            `Block ${baseBlock.key} was not found under its base parent`
        );
    }

    const nextSiblingKey = baseSiblingKeys
        .slice(baseIndex + 1)
        .find((siblingKey) => currentParent.getChildIndex(siblingKey) >= 0);
    if (nextSiblingKey) {
        return {
            parentKey: currentParent.key,
            index: currentParent.getChildIndex(nextSiblingKey),
        };
    }

    const previousSiblingKey = baseSiblingKeys
        .slice(0, baseIndex)
        .findLast((siblingKey) => currentParent.getChildIndex(siblingKey) >= 0);
    if (previousSiblingKey) {
        return {
            parentKey: currentParent.key,
            index: currentParent.getChildIndex(previousSiblingKey) + 1,
        };
    }

    return {
        parentKey: currentParent.key,
        index: currentParent.nodes.size,
    };
}

function isDocumentBlockParent(node: unknown): node is Document | Block | Fragment {
    return Document.isDocument(node) || Block.isBlock(node) || Fragment.isFragment(node);
}

function assertNoKeyConflict(document: Document, block: Block): void {
    if (!document.hasNodeKeyConflict(block)) {
        return;
    }

    throw new RevertDocumentBlockChangeError(
        'key_conflict',
        `Cannot restore block ${block.key} because one of its keys already exists in the current document`
    );
}

function pathsEqual(left: readonly number[] | null, right: readonly number[] | null): boolean {
    if (!left || !right || left.length !== right.length) {
        return false;
    }

    return left.every((part, index) => part === right[index]);
}
